import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import DataTable from '../components/DataTable';

const ListarMatriculados = () => {
  const [detalles, setDetalles] = useState([]);
  const [cargando, setCargando] = useState(false);
  const navigate = useNavigate();

  // ID de matrícula desde la ruta
  const { id: matriculaId } = useParams();

  // Ver detalle del aprendiz
  const verDetalle = useCallback(
    (idAprendiz) => {
      if (idAprendiz) navigate(`/aprendiz/${idAprendiz}`);
    },
    [navigate]
  );

  // Ir a registrar etapa productiva (aprendizId + matriculaId)
  const irEtapaProductiva = useCallback(
    (idAprendiz) => {
      if (!idAprendiz) {
        return Swal.fire({
          icon: 'warning',
          title: 'ID no encontrado',
          text: 'No se encontró el ID del aprendiz.',
        });
      }

      if (!matriculaId) {
        return Swal.fire({
          icon: 'warning',
          title: 'Matrícula no encontrada',
          text: 'No se encontró el ID de la matrícula en la ruta actual.',
        });
      }

      navigate(`/etapaproductiva/registro/${idAprendiz}/${matriculaId}`);
    },
    [navigate, matriculaId]
  );

  // ✅ Eliminar detalle (SIN llamar getData) para evitar ciclos y warnings
  const deleteDetalleMatricula = useCallback(async (idDetalle) => {
    try {
      const res = await Swal.fire({
        title: '¿Está seguro?',
        text: 'Este registro será eliminado permanentemente.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
      });

      if (!res.isConfirmed) return;

      const { data } = await axios.delete(`/api/detallematricula/delete/${idDetalle}`);

      if (data?.ok) {
        await Swal.fire({
          icon: 'success',
          title: data.message || 'Eliminado correctamente',
          showConfirmButton: false,
          timer: 1500,
        });

        // ✅ Actualización optimista
        setDetalles((prev) => prev.filter((d) => d._id !== idDetalle));
      } else {
        Swal.fire('Error', 'No se pudo eliminar el detalle.', 'error');
      }
    } catch (error) {
      Swal.fire(
        'Error',
        error.response?.data?.message || error.message,
        'error'
      );
    }
  }, []);

  // Obtener datos
  const getData = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await axios.get(`/api/detallematricula/listbyid/${matriculaId}`);

      if (data?.ok && Array.isArray(data.detalles)) {
        setDetalles(
          data.detalles.map((detalle) => ({
            ...detalle,
            nombreCompleto: detalle.nombreAprendiz || '—',

            // ✅ Iconos siempre horizontales
            funciones: (
              <div className="d-flex align-items-center justify-content-center gap-2 flex-nowrap">
                <button
                  className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  onClick={() => verDetalle(detalle.aprendizObjectId?._id)}
                  title="Ver aprendiz"
                  type="button"
                >
                  <i className="fa-solid fa-circle-info" />
                </button>

                <button
                  className="btn btn-dark btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  onClick={() => irEtapaProductiva(detalle.aprendizObjectId?._id)}
                  title="Registrar etapa productiva"
                  type="button"
                >
                  <i className="fa-solid fa-building" />
                </button>

                <button
                  className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  onClick={() => deleteDetalleMatricula(detalle._id)}
                  title="Eliminar detalle de matrícula"
                  type="button"
                >
                  <i className="fa-solid fa-trash" />
                </button>
              </div>
            ),
          }))
        );
      } else {
        setDetalles([]);
      }
    } catch (error) {
      console.error('Error al cargar los detalles:', error);
      Swal.fire(
        'Error',
        error.response?.data?.message || error.message,
        'error'
      );
      setDetalles([]);
    } finally {
      setCargando(false);
    }
  }, [matriculaId, verDetalle, irEtapaProductiva, deleteDetalleMatricula]);

  useEffect(() => {
    if (matriculaId) getData();
  }, [matriculaId, getData]);

  const columns = useMemo(
    () => [
      { Header: 'Ficha', accessor: 'fichaGrupo' },
      { Header: 'Documento', accessor: 'documentoAprendiz' },
      { Header: 'Nombre Completo', accessor: 'nombreCompleto' },
      { Header: 'Estado', accessor: 'estadoMatricula' },
      { Header: 'Tipo', accessor: 'tipoMatricula' },
      { accessor: 'funciones' },
    ],
    []
  );

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">Lista de matriculados</h5>
                <small className="text-secondary">
                  Gestión de aprendices asociados a la matrícula.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate(-1)}
                  title="Volver">
                   <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  disabled={cargando}
                  title="Actualizar">
                  <i className={`fa-solid ${cargando ? 'fa-spinner fa-spin' : 'fa-rotate'} me-2`} />
                  Actualizar
                </button>
              </div>

            </div>
          </div>

          {/* Body */}
          <div className="p-3 p-md-4">
            {cargando ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" role="status" aria-hidden="true"></div>
                <div className="mt-3">Cargando matriculados…</div>
              </div>
            ) : Array.isArray(detalles) && detalles.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={detalles} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">🐾</div>
                <div className="fw-semibold">No se encontraron registros</div>
                <div className="small">No hay aprendices asociados a esta matrícula.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarMatriculados;
